// Widget "Tareas pendientes" del Dashboard (Rediseño mayor 2026-09-01) —
// lista personal del practicante logueado. El widget compacto (siempre
// visible) deja tildar directo las primeras pendientes; clickear el TÍTULO
// abre el panel completo: agregar (texto + fecha/hora opcional), y dos
// pestañas (Pendientes/Completadas) — tildar una pendiente la pasa a
// Completadas con un ✅ momentáneo antes de moverla; una completada puede
// volver a pendiente o borrarse directo.
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  actualizarTareaDashboard,
  borrarTareaDashboard,
  crearTareaDashboard,
  listarTareasDashboard,
  type TareaDashboard,
} from '@/lib/hceVault';

function formatoFechaHora(iso: string): string {
  return new Date(iso).toLocaleString('es-EC', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false });
}

export function TareasPendientes(): JSX.Element {
  const { t } = useTranslation();
  const [tareas, setTareas] = useState<TareaDashboard[] | null>(null);
  const [abierto, setAbierto] = useState(false);
  const [pestana, setPestana] = useState<'pendientes' | 'completadas'>('pendientes');
  const [recienCompletada, setRecienCompletada] = useState<number | null>(null);
  const [nuevoTexto, setNuevoTexto] = useState('');
  const [nuevaFechaHora, setNuevaFechaHora] = useState('');
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      setTareas(await listarTareasDashboard());
    } catch {
      setTareas([]);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const pendientes = (tareas ?? []).filter((tarea) => !tarea.completada);
  const completadas = (tareas ?? []).filter((tarea) => tarea.completada);

  async function alternarCompletada(tarea: TareaDashboard): Promise<void> {
    if (!tarea.completada) {
      setRecienCompletada(tarea.id);
      window.setTimeout(() => setRecienCompletada((actual) => (actual === tarea.id ? null : actual)), 700);
    }
    await actualizarTareaDashboard(tarea.id, { completada: !tarea.completada });
    await cargar();
  }

  async function agregar(): Promise<void> {
    const texto = nuevoTexto.trim();
    if (!texto) return;
    setGuardando(true);
    try {
      await crearTareaDashboard(texto, nuevaFechaHora ? new Date(nuevaFechaHora).toISOString() : null);
      setNuevoTexto('');
      setNuevaFechaHora('');
      await cargar();
    } finally {
      setGuardando(false);
    }
  }

  async function borrar(id: number): Promise<void> {
    await borrarTareaDashboard(id);
    await cargar();
  }

  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <button type="button" onClick={() => setAbierto(true)} className="text-sm font-medium text-hce-ink hover:text-hce-pine hover:underline">
        {t('dashboard.tareas.titulo')}
      </button>

      <ul className="mt-2 space-y-1.5">
        {pendientes.length === 0 && <li className="text-xs text-hce-muted">{t('dashboard.tareas.sinPendientes')}</li>}
        {pendientes.slice(0, 5).map((tarea) => (
          <li key={tarea.id} className="flex items-center gap-2">
            <Checkbox checked={recienCompletada === tarea.id} onCheckedChange={() => void alternarCompletada(tarea)} />
            <span className="truncate text-sm text-hce-ink">{tarea.texto}</span>
            {recienCompletada === tarea.id && <span aria-hidden="true">✅</span>}
          </li>
        ))}
      </ul>

      <Dialog open={abierto} onOpenChange={setAbierto}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('dashboard.tareas.titulo')}</DialogTitle>
          </DialogHeader>

          <Tabs value={pestana} onValueChange={(v) => setPestana(v as 'pendientes' | 'completadas')}>
            <TabsList>
              <TabsTrigger value="pendientes">
                {t('dashboard.tareas.pendientes')} ({pendientes.length})
              </TabsTrigger>
              <TabsTrigger value="completadas">
                {t('dashboard.tareas.completadas')} ({completadas.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="pendientes" className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  value={nuevoTexto}
                  onChange={(e) => setNuevoTexto(e.target.value)}
                  placeholder={t('dashboard.tareas.nuevaPlaceholder')}
                  className="min-w-[10rem] flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void agregar();
                  }}
                />
                <Input type="datetime-local" value={nuevaFechaHora} onChange={(e) => setNuevaFechaHora(e.target.value)} className="w-auto" />
                <Button size="sm" disabled={guardando || !nuevoTexto.trim()} onClick={() => void agregar()}>
                  {t('dashboard.tareas.agregar')}
                </Button>
              </div>

              <ul className="max-h-72 divide-y divide-border overflow-y-auto">
                {pendientes.length === 0 && <li className="py-3 text-sm text-hce-muted">{t('dashboard.tareas.sinPendientes')}</li>}
                {pendientes.map((tarea) => (
                  <li key={tarea.id} className="flex items-center gap-2 py-2">
                    <Checkbox checked={recienCompletada === tarea.id} onCheckedChange={() => void alternarCompletada(tarea)} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm text-hce-ink">{tarea.texto}</div>
                      {tarea.fechaHora && <div className="text-xs text-hce-muted">{formatoFechaHora(tarea.fechaHora)}</div>}
                    </div>
                    {recienCompletada === tarea.id && <span aria-hidden="true">✅</span>}
                    <Button variant="ghost" size="sm" onClick={() => void borrar(tarea.id)}>
                      {t('dashboard.tareas.borrar')}
                    </Button>
                  </li>
                ))}
              </ul>
            </TabsContent>

            <TabsContent value="completadas">
              <ul className="max-h-80 divide-y divide-border overflow-y-auto">
                {completadas.length === 0 && <li className="py-3 text-sm text-hce-muted">{t('dashboard.tareas.sinCompletadas')}</li>}
                {completadas.map((tarea) => (
                  <li key={tarea.id} className="flex items-center gap-2 py-2">
                    <span aria-hidden="true">✅</span>
                    <span className="min-w-0 flex-1 truncate text-sm text-hce-muted line-through">{tarea.texto}</span>
                    <Button variant="outline" size="sm" onClick={() => void alternarCompletada(tarea)}>
                      {t('dashboard.tareas.volverAPendiente')}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => void borrar(tarea.id)}>
                      {t('dashboard.tareas.borrar')}
                    </Button>
                  </li>
                ))}
              </ul>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
